// Constantes sector — secteur d'activité du membre.
//
// Source de vérité côté frontend pour les labels FR.
// Aligné sur backend/config/sectors.js (slugs identiques, ordre identique).
//
// Si tu ajoutes/retires un secteur, propage dans :
//   1. backend/config/sectors.js (SECTORS)
//   (pas de migration : la colonne est un VARCHAR validé par isValidSector)

// `usableAsCompanyIndustry` : miroir du flag backend (config/sectors.js). false pour
// les slugs qui sont des FONCTIONS et non des industries (marketing, rh) → évite la
// collision sémantique avec job_family quand on s'en sert comme company_industry.
export const SECTORS = [
  { slug: 'aeronautique', usableAsCompanyIndustry: true },
  { slug: 'tech', usableAsCompanyIndustry: true },
  { slug: 'finance', usableAsCompanyIndustry: true },
  { slug: 'conseil', usableAsCompanyIndustry: true },
  { slug: 'sante', usableAsCompanyIndustry: true },
  { slug: 'industrie', usableAsCompanyIndustry: true },
  { slug: 'btp', usableAsCompanyIndustry: true },
  { slug: 'energie', usableAsCompanyIndustry: true },
  { slug: 'commerce', usableAsCompanyIndustry: true },
  { slug: 'transport', usableAsCompanyIndustry: true },
  { slug: 'agroalimentaire', usableAsCompanyIndustry: true },
  { slug: 'automobile', usableAsCompanyIndustry: true },
  { slug: 'telecoms', usableAsCompanyIndustry: true },
  { slug: 'medias', usableAsCompanyIndustry: true },
  { slug: 'marketing', usableAsCompanyIndustry: false },
  { slug: 'juridique', usableAsCompanyIndustry: true },
  { slug: 'rh', usableAsCompanyIndustry: false },
  { slug: 'education', usableAsCompanyIndustry: true },
  { slug: 'recherche', usableAsCompanyIndustry: true },
  { slug: 'public', usableAsCompanyIndustry: true },
  { slug: 'ess', usableAsCompanyIndustry: true },
  { slug: 'immobilier', usableAsCompanyIndustry: true },
  { slug: 'tourisme', usableAsCompanyIndustry: true },
  { slug: 'environnement', usableAsCompanyIndustry: true },
  { slug: 'sport', usableAsCompanyIndustry: true },
  { slug: 'art-culture', usableAsCompanyIndustry: true },
  { slug: 'mode-luxe', usableAsCompanyIndustry: true },
  { slug: 'autre', usableAsCompanyIndustry: true },
]

export const SECTOR_VALUES = SECTORS.map((s) => s.slug)

// Sous-ensemble « industrie de l'employeur » pour company_industry (dérivé du flag,
// source unique — pas de liste d'exclusion dupliquée).
export const COMPANY_INDUSTRIES = SECTORS.filter((s) => s.usableAsCompanyIndustry === true)
export const COMPANY_INDUSTRY_VALUES = COMPANY_INDUSTRIES.map((s) => s.slug)

export const SECTOR_LABELS = {
  aeronautique: 'Aéronautique & Spatial',
  tech: 'Tech & Numérique / IT',
  finance: 'Banque / Assurance / Finance',
  conseil: 'Conseil / Audit',
  sante: 'Santé / Pharma',
  industrie: 'Industrie',
  btp: 'BTP / Construction',
  energie: 'Énergie',
  commerce: 'Commerce / Distribution',
  transport: 'Transport / Logistique',
  agroalimentaire: 'Agroalimentaire',
  automobile: 'Automobile',
  telecoms: 'Télécoms',
  medias: 'Médias / Communication',
  marketing: 'Marketing / Publicité',
  juridique: 'Juridique',
  rh: 'Ressources Humaines',
  education: 'Éducation / Formation',
  recherche: 'Recherche / Sciences',
  public: 'Secteur public / Administration',
  ess: 'Associatif / ESS',
  immobilier: 'Immobilier',
  tourisme: 'Tourisme / Hôtellerie / Restauration',
  environnement: 'Environnement / Développement durable',
  sport: 'Sport',
  'art-culture': 'Art / Culture / Divertissement',
  'mode-luxe': 'Mode / Luxe',
  autre: 'Autre',
}

// Couleur d'accent par secteur (liseret carte + chip annuaire). Palette sobre
// et distincte. NE PAS confondre avec l'anneau career_status de l'avatar (vert).
export const SECTOR_COLORS = {
  aeronautique: '#2563eb',
  tech: '#6366f1',
  finance: '#16a34a',
  conseil: '#0891b2',
  sante: '#0d9488',
  industrie: '#78716c',
  btp: '#ea580c',
  energie: '#ca8a04',
  commerce: '#d946ef',
  transport: '#0ea5e9',
  agroalimentaire: '#65a30d',
  automobile: '#1e40af',
  telecoms: '#7c3aed',
  medias: '#db2777',
  marketing: '#f43f5e',
  juridique: '#475569',
  rh: '#059669',
  education: '#0284c7',
  recherche: '#9333ea',
  public: '#334155',
  ess: '#14b8a6',
  immobilier: '#b45309',
  tourisme: '#06b6d4',
  environnement: '#22c55e',
  sport: '#e11d48',
  'art-culture': '#c026d3',
  'mode-luxe': '#a21caf',
  autre: '#64748b',
}

// Couleur d'un secteur (slug). Vide/inconnu → gris neutre.
export const getSectorColor = (slug) => (slug && SECTOR_COLORS[slug]) || '#cbd5e1'

// Label affichable d'un secteur (slug). Avec `t` → i18n (business:sector.<slug>,
// fallback FR via defaultValue) ; sans `t` → map FR. Vide/null → '' (caller décide
// du fallback). SECTOR_LABELS reste le fallback data-safe (hors React / exports).
export const getSectorLabel = (slug, t) => {
  if (!slug) return ''
  if (t) return t(`business:sector.${slug}`, { defaultValue: SECTOR_LABELS[slug] || '' })
  return SECTOR_LABELS[slug] || ''
}

export const isValidSector = (value) =>
  value === null || value === undefined || value === '' || SECTOR_VALUES.includes(value)
